// src/context/UploadContext.js
// Upload state and logic that persists across navigation (UserCaptures -> CaptureViewer).

import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { doc, getDoc, serverTimestamp, onSnapshot, deleteField } from 'firebase/firestore';
import { patchCaptureDoc } from '../utils/captureFirestorePatch';
import { httpsCallable } from 'firebase/functions';
import { auth, db, functions, storage } from '../firebase';
import { ref as storageRef, uploadBytes } from 'firebase/storage';
import {
  uploadVideoResumableWithRetry,
  formatVideoUploadErrorMessage,
} from '../utils/resumableVideoUpload';
import {
  messageToStage,
  inferFailStageFromError,
  getFailedStageIndex,
  normalizeProgressMessage,
  mapRedisStageToFrontend,
  isWorkerQueueingSignal,
  isCapturePipelineWorkerActive,
  isStaleFirestorePipelineFailure,
  readCaptureFrameFields,
  hasWorkerFoundEvidence,
  resolveCanonicalPipelineStage,
  resolveFailureDetailCalloutText,
  UPLOAD_INTERRUPTED_MESSAGE,
} from '../utils/uploadProgress';
import {
  UPLOAD_AND_PIPELINE_TIMEOUT_MS,
  BUTTON_REQUEST_TIMEOUT_MS,
  SET_CAPTURE_STATUS_FAILED_THROTTLE_MS,
  RUNPOD_STUCK_REDISPATCH_MS,
  UPLOAD_BYTE_STALL_DETECT_MS,
} from '../constants/timeouts';
import { getBackendBaseUrl, isDispatchedGpuMode, fetchPipelineStatusAuthHeaders } from '../utils/backendUrl';
import { assertCapturesStorageHeadroom } from '../utils/capturesStorageGate';
import { captureStoragePrefix, normalizedProjectId } from '../constants/studioProjects';
import { SOURCE_FORMAT } from '../constants/captureSchema';
import { readVideoFileSourceMetadata } from '../utils/readVideoFileDuration';
import toast from 'react-hot-toast';
import { extractVideoMetadata } from '../utils/extractVideoMetadata';
import {
  TRANSPARENT_PROCESSING_FAILURE_MESSAGE,
  callableErrorMessage,
  dispatchCallableFailureDetail,
  isRetryableDispatchCallableError,
  DISPATCH_FAILED_FAILURE_CODE,
} from '../utils/firebaseErrorMessage';
import { createPipelineTimeline, logPipelineDiagnostic } from '../utils/pipelineDiagnostics';
import { removeShopifyProcessingEntry } from '../utils/shopifyActiveUpload';
import { buildFailureFirestorePatch, isVerboseFailureText } from '../utils/failureMetadata';
import { captureHasReconstructionArtifact } from '../utils/viewerReadiness';
import { clearPipelinePollCache } from '../utils/pipelinePollCache';

const UploadContext = createContext(null);

const IS_DISPATCHED_MODE = isDispatchedGpuMode();

const UPLOAD_PROGRESS_THROTTLE_MS = 100;

function getPipelineStageTitle(
  message,
  stage,
  _sfmPhase,
  final,
  pipelinePercent,
  processingStatus = null,
  pipelineStage = null,
  framesExtracted = null,
) {
  if (final === 'viewer-ready') return 'RECONSTRUCTION COMPLETE';
  if (final === 'gaussian-complete') return 'GAUSSIAN TRAINING';
  if (typeof stage === 'number' && !Number.isNaN(stage) && stage < 0) return 'QUEUEING';
  const canonical = resolveCanonicalPipelineStage({
    processingStatus,
    pipelineStage,
    progressPercent: pipelinePercent,
    progressMessage: message,
    framesExtracted,
  });
  if (canonical.isQueueing) return 'QUEUEING';
  if (canonical.title && canonical.title !== 'PROCESSING') return canonical.title;
  // Fall back to Redis/internal stage index when message-only inference is still generic.
  if (typeof stage === 'number' && !Number.isNaN(stage)) {
    if (stage === 2) return 'IMAGE EXTRACTION';
    if (stage === 3) return 'ANALYZING IMAGES';
    if (stage === 4) return 'GAUSSIAN TRAINING';
    if (stage === 5) return 'FINALIZING';
    if (stage >= 6) return 'RECONSTRUCTION COMPLETE';
    if (stage === 1) return 'UPLOADING VIDEO';
  }
  return canonical.title || 'PROCESSING';
}

function deriveVideoNameFromCaptureId(captureId) {
  if (!captureId || typeof captureId !== 'string') return 'video';
  const idx = captureId.lastIndexOf('-');
  if (idx <= 0) return captureId;
  return captureId.slice(0, idx);
}

const UPLOAD_SESSION_KEY = 'volugraph_upload_state';

function saveUploadSession(state) {
  try { sessionStorage.setItem(UPLOAD_SESSION_KEY, JSON.stringify(state)); } catch {}
}
function loadUploadSession() {
  try {
    const raw = sessionStorage.getItem(UPLOAD_SESSION_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

const STUDIO_COLLECTION = 'studio';
const CAPTURES_SUB = 'captures';
function clearUploadSession() {
  try { sessionStorage.removeItem(UPLOAD_SESSION_KEY); } catch {}
}

export function UploadProvider({ children }) {
  const restored = useRef(loadUploadSession()).current;
  const [activeCaptureId, setActiveCaptureId] = useState(restored?.activeCaptureId ?? null);
  const [activeProjectId, setActiveProjectId] = useState(restored?.activeProjectId ?? null);
  const [activeShopifyProductId, setActiveShopifyProductId] = useState(restored?.activeShopifyProductId ?? null);
  const [isUploading, setIsUploading] = useState(restored?.isUploading ?? false);
  const [uploadPhase, setUploadPhase] = useState(restored?.uploadPhase ?? 'idle');
  const [uploadPercent, setUploadPercent] = useState(restored?.uploadPercent ?? 0);
  const [pipelinePercent, setPipelinePercent] = useState(restored?.pipelinePercent ?? 0);
  const [uploadBytesTransferred, setUploadBytesTransferred] = useState(restored?.uploadBytesTransferred ?? 0);
  const [uploadTotalBytes, setUploadTotalBytes] = useState(restored?.uploadTotalBytes ?? 0);
  const [queuePosition, setQueuePosition] = useState(restored?.queuePosition ?? null);
  const [progressPercent, setProgressPercent] = useState(restored?.progressPercent ?? 0);
  const [progressMessage, setProgressMessage] = useState(restored?.progressMessage ?? 'Preparing upload');
  const [currentStage, setCurrentStage] = useState(restored?.currentStage ?? 0);
  const [sfmConductingPhase, setSfmConductingPhase] = useState(null);
  const [finalPhase, setFinalPhase] = useState(null);
  const [uploadError, setUploadError] = useState(null);
  const [framesExtracted, setFramesExtracted] = useState(null);
  const sfmTimeoutRef = useRef(null);
  const finalTimeoutRef = useRef(null);
  const abortControllerRef = useRef(null);
  const uploadTaskRef = useRef(null);
  const uploadProgressThrottleRef = useRef(null);
  const uploadProgressSnapshotRef = useRef({ transferred: 0, total: 0 });
  const uploadPhaseRef = useRef('idle');
  const workerProgressStartedRef = useRef(false);
  const runpodJobStatusRef = useRef(null);
  const lastCaptureProcessingStatusRef = useRef(null);
  const lastCapturePipelineStageRef = useRef(null);
  const activeCaptureIdRef = useRef(null);
  const currentStageRef = useRef(0);
  const lastBackendEventAtRef = useRef(0);
  const lastConfirmedPercentRef = useRef(0);
  const lastConfirmedEventAtRef = useRef(0);
  const pipelinePercentRef = useRef(0);
  const uploadPercentRef = useRef(0);
  const stageEnteredAtRef = useRef(0);
  const statusFailedThrottleRef = useRef({ captureId: null, at: 0 });
  const firestoreUnsubRef = useRef(null);
  const stuckCheckTimeoutRef = useRef(null);
  const uploadStallTimeoutRef = useRef(null);
  const lastUploadByteProgressAtRef = useRef(0);
  const restoredSessionRef = useRef(restored);
  const activeProjectIdRef = useRef(null);
  const activeStudioUidRef = useRef(restored?.activeStudioUid ?? null);
  const pipelineTimelineRef = useRef(null);
  const prevUploadPhaseRef = useRef('idle');

  const isLocalhost =
    typeof window !== 'undefined' &&
    (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
  const backendBaseUrl = getBackendBaseUrl();

  uploadPhaseRef.current = uploadPhase;
  pipelinePercentRef.current = pipelinePercent;
  uploadPercentRef.current = uploadPercent;

  const effectiveProgressPercent = uploadPhase === 'uploading' ? uploadPercent : pipelinePercent;
  const stageTitle = uploadPhase === 'uploading'
    ? 'UPLOADING VIDEO'
    : (uploadPhase === 'queueing'
      ? (queuePosition != null ? `QUEUEING (${queuePosition} ahead)` : 'QUEUEING')
      : getPipelineStageTitle(
        progressMessage,
        currentStage,
        sfmConductingPhase,
        finalPhase,
        pipelinePercent,
        lastCaptureProcessingStatusRef.current,
        lastCapturePipelineStageRef.current,
        framesExtracted,
      ));
  currentStageRef.current = currentStage;

  useEffect(() => {
    if (uploadPhase !== 'uploading') return undefined;
    if (typeof window !== 'undefined' && window.location.pathname.startsWith('/shopify')) {
      return undefined;
    }
    const handler = (e) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [uploadPhase]);

  useEffect(() => {
    activeProjectIdRef.current = activeProjectId;
  }, [activeProjectId]);

  useEffect(() => {
    setProgressPercent(effectiveProgressPercent);
  }, [effectiveProgressPercent]);

  useEffect(() => {
    const prev = prevUploadPhaseRef.current;
    if (prev === 'queueing' && uploadPhase === 'pipeline') {
      logPipelineDiagnostic('ui_queueing_to_pipeline', {
        captureId: activeCaptureId,
        projectId: activeProjectId,
      });
      pipelineTimelineRef.current?.mark('ui_queueing_to_pipeline');
    }
    prevUploadPhaseRef.current = uploadPhase;
  }, [uploadPhase, activeCaptureId, activeProjectId]);

  useEffect(() => {
    if (activeCaptureId && (isUploading || effectiveProgressPercent > 0 || uploadPhase !== 'idle')) {
      saveUploadSession({
        activeCaptureId,
        activeProjectId,
        activeShopifyProductId,
        activeStudioUid: activeStudioUidRef.current,
        isUploading,
        uploadPhase,
        uploadPercent,
        pipelinePercent,
        uploadBytesTransferred,
        uploadTotalBytes,
        queuePosition,
        progressPercent: effectiveProgressPercent,
        progressMessage,
        currentStage,
      });
    } else if (!activeCaptureId) {
      clearUploadSession();
    }
  }, [
    activeCaptureId,
    activeProjectId,
    activeShopifyProductId,
    isUploading,
    uploadPhase,
    uploadPercent,
    pipelinePercent,
    uploadBytesTransferred,
    uploadTotalBytes,
    queuePosition,
    effectiveProgressPercent,
    progressMessage,
    currentStage,
  ]);

  /** No-op: previously cleared synthetic progress tick (removed; ring shows server percent only). */
  const clearHeartbeat = useCallback(() => {}, []);

  const cleanupFirestoreListener = useCallback(() => {
    if (firestoreUnsubRef.current) {
      firestoreUnsubRef.current();
      firestoreUnsubRef.current = null;
    }
  }, []);

  /* ------------------------------------------------------------------ */
  /*  cancelUpload                                                       */
  /* ------------------------------------------------------------------ */

  const cancelUpload = useCallback(async (captureIdOverride, cancelOptions = {}) => {
    if (stuckCheckTimeoutRef.current) {
      clearTimeout(stuckCheckTimeoutRef.current);
      stuckCheckTimeoutRef.current = null;
    }
    if (uploadTaskRef.current) {
      try { uploadTaskRef.current.cancel(); } catch (_) { /* ignore */ }
      uploadTaskRef.current = null;
    }
    if (uploadProgressThrottleRef.current) {
      clearTimeout(uploadProgressThrottleRef.current);
      uploadProgressThrottleRef.current = null;
    }
    const cid = captureIdOverride ?? activeCaptureIdRef.current ?? activeCaptureId;
    const cancelProjectId =
      cancelOptions.projectId ?? activeProjectIdRef.current ?? undefined;
    const cancelStudioUid =
      cancelOptions.studioUid ?? activeStudioUidRef.current ?? undefined;
    let cancelResult = { ok: true, terminated: false, message: 'No active pipeline' };
    if (cid) {
      try {
        if (IS_DISPATCHED_MODE) {
          const cancelCaptureFn = httpsCallable(functions, 'cancelCapture');
          const res = await cancelCaptureFn({
            captureId: cid,
            projectId: cancelProjectId,
            studioUid: cancelStudioUid,
          });
          cancelResult = res.data;
        } else {
          const token = await auth.currentUser?.getIdToken?.();
          const cancelController = new AbortController();
          const cancelTimeoutId = setTimeout(() => cancelController.abort(), BUTTON_REQUEST_TIMEOUT_MS);
          const res = await fetch(`${backendBaseUrl}/cancel`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
            body: JSON.stringify({ captureId: cid }),
            signal: cancelController.signal,
          });
          clearTimeout(cancelTimeoutId);
          const payload = await res.json().catch(() => ({}));
          if (!res.ok) {
            cancelResult = {
              ok: false,
              terminated: false,
              message: payload?.error || `Cancel request failed (${res.status})`,
            };
          } else {
            cancelResult = {
              ok: true,
              terminated: !!payload?.terminated,
              message: payload?.message || 'Cancel requested',
            };
          }
        }
      } catch (e) {
        cancelResult = {
          ok: false,
          terminated: false,
          message: (e?.name === 'AbortError' ? 'Request timed out' : null) || e?.message || 'Cancel request failed',
        };
      }
    }
    if (abortControllerRef.current) abortControllerRef.current.abort();
    clearHeartbeat();
    cleanupFirestoreListener();
    setIsUploading(false);
    setUploadError(null);
    setUploadPhase('idle');
    workerProgressStartedRef.current = false;
    return cancelResult;
  }, [activeCaptureId, backendBaseUrl, clearHeartbeat, cleanupFirestoreListener]);

  activeCaptureIdRef.current = activeCaptureId;

  const clearUpload = useCallback(() => {
    if (uploadTaskRef.current) {
      try { uploadTaskRef.current.cancel(); } catch (_) { /* ignore */ }
      uploadTaskRef.current = null;
    }
    if (uploadProgressThrottleRef.current) {
      clearTimeout(uploadProgressThrottleRef.current);
      uploadProgressThrottleRef.current = null;
    }
    const now = Date.now();
    clearHeartbeat();
    cleanupFirestoreListener();
    clearUploadSession();
    setActiveCaptureId(null);
    setActiveProjectId(null);
    setActiveShopifyProductId(null);
    setIsUploading(false);
    setUploadPhase('idle');
    setUploadPercent(0);
    setPipelinePercent(0);
    setUploadBytesTransferred(0);
    setUploadTotalBytes(0);
    setQueuePosition(null);
    setProgressPercent(0);
    setProgressMessage('Preparing upload');
    setCurrentStage(0);
    setSfmConductingPhase(null);
    setFinalPhase(null);
    setUploadError(null);
    setFramesExtracted(null);
    workerProgressStartedRef.current = false;
    runpodJobStatusRef.current = null;
    lastBackendEventAtRef.current = now;
    lastConfirmedEventAtRef.current = now;
    lastConfirmedPercentRef.current = 0;
    stageEnteredAtRef.current = now;
  }, [clearHeartbeat, cleanupFirestoreListener]);

  useEffect(() => {
    return () => {
      clearHeartbeat();
      cleanupFirestoreListener();
      if (uploadStallTimeoutRef.current) {
        clearTimeout(uploadStallTimeoutRef.current);
        uploadStallTimeoutRef.current = null;
      }
    };
  }, [clearHeartbeat, cleanupFirestoreListener]);

  const clearUploadStallWatch = useCallback(() => {
    if (uploadStallTimeoutRef.current) {
      clearTimeout(uploadStallTimeoutRef.current);
      uploadStallTimeoutRef.current = null;
    }
  }, []);

  const markUploadInterrupted = useCallback(async (message = UPLOAD_INTERRUPTED_MESSAGE) => {
    const cid = activeCaptureIdRef.current;
    const pid = activeProjectIdRef.current;
    const uid = activeStudioUidRef.current || auth.currentUser?.uid || null;
    if (uploadTaskRef.current) {
      try { uploadTaskRef.current.cancel(); } catch (_) { /* ignore */ }
      uploadTaskRef.current = null;
    }
    clearUploadStallWatch();
    cleanupFirestoreListener();
    clearHeartbeat();
    setUploadError(message);
    setUploadPhase('failed');
    setProgressMessage('Error');
    setIsUploading(false);
    if (cid && uid && pid) {
      const captureRef = doc(db, 'users', uid, STUDIO_COLLECTION, pid, CAPTURES_SUB, cid);
      let skipFirestoreFailureWrite = false;
      try {
        const snap = await getDoc(captureRef);
        if (snap.exists()) {
          const data = snap.data() || {};
          const procStatus = String(data.processing?.status || '').toLowerCase();
          if (procStatus === 'completed' || captureHasReconstructionArtifact(data)) {
            skipFirestoreFailureWrite = true;
          }
        }
      } catch (e) {
        console.warn('Could not read capture before upload-interrupted patch', e);
      }
      const failedPercent = Math.round(
        Math.max(
          0,
          Number.isFinite(uploadPercentRef.current) ? uploadPercentRef.current : 0,
        ),
      );
      if (!skipFirestoreFailureWrite) {
        try {
          await patchCaptureDoc(captureRef, {
            ...buildFailureFirestorePatch({
              status: 'failed',
              failedAtRaw: 'Upload',
              failedPercent,
              progressPercent: failedPercent,
              failureDetail: message,
            }),
            updatedAt: serverTimestamp(),
            status: deleteField(),
            failedAt: deleteField(),
            failStatus: deleteField(),
          });
        } catch (e) {
          console.error('Failed to mark interrupted upload as failed', e);
        }
      }
    }
    const shopifyProductId =
      loadUploadSession()?.activeShopifyProductId || activeShopifyProductId;
    setActiveCaptureId(null);
    setActiveProjectId(null);
    setActiveShopifyProductId(null);
    if (shopifyProductId) {
      removeShopifyProcessingEntry(shopifyProductId);
    }
    clearUploadSession();
  }, [clearHeartbeat, clearUploadStallWatch, cleanupFirestoreListener, activeShopifyProductId]);

  const scheduleUploadStallWatch = useCallback(() => {
    clearUploadStallWatch();
    uploadStallTimeoutRef.current = setTimeout(() => {
      uploadStallTimeoutRef.current = null;
      if (uploadPhaseRef.current !== 'uploading') return;
      if (!activeCaptureIdRef.current) return;
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') {
        scheduleUploadStallWatch();
        return;
      }
      const elapsed = Date.now() - (lastUploadByteProgressAtRef.current || 0);
      if (elapsed < UPLOAD_BYTE_STALL_DETECT_MS) {
        scheduleUploadStallWatch();
        return;
      }
      if (!uploadTaskRef.current) {
        void markUploadInterrupted();
        return;
      }
      if ((uploadPercentRef.current ?? 0) < 100) {
        void markUploadInterrupted();
      }
    }, UPLOAD_BYTE_STALL_DETECT_MS);
  }, [clearUploadStallWatch, markUploadInterrupted]);

  /** Session restored after reload: in-memory upload task is gone — fail fast instead of stuck 0%. */
  useEffect(() => {
    const session = restoredSessionRef.current;
    if (!session?.isUploading || !session?.activeCaptureId) return;
    if (session.uploadPhase !== 'uploading') return;
    if (uploadTaskRef.current) return;
    const run = () => {
      if (!auth.currentUser?.uid && !session.activeStudioUid) return;
      void markUploadInterrupted();
    };
    if (auth.currentUser?.uid) {
      run();
      return undefined;
    }
    const unsub = auth.onAuthStateChanged((user) => {
      if (user?.uid) {
        unsub();
        run();
      }
    });
    return () => unsub();
  }, [markUploadInterrupted]);

  /**
   * After reload, queueing/pipeline session snapshots are stale (no upload task / listener).
   * Drop the overlay so Shopify cards + process modal use pipeline polling + Firestore instead.
   */
  useEffect(() => {
    const session = restoredSessionRef.current;
    if (!session?.activeCaptureId) return;
    if (session.uploadPhase !== 'queueing' && session.uploadPhase !== 'pipeline') return;
    restoredSessionRef.current = null;
    setIsUploading(false);
    setUploadPhase('idle');
    setPipelinePercent(0);
    setProgressPercent(0);
    setProgressMessage('');
    setCurrentStage(0);
    setQueuePosition(null);
    if (session.activeShopifyProductId) {
      removeShopifyProcessingEntry(session.activeShopifyProductId);
    }
    setActiveCaptureId(null);
    setActiveProjectId(null);
    setActiveShopifyProductId(null);
    clearUploadSession();
  }, []);

  useEffect(() => {
    if (uploadPhase !== 'uploading' || !activeCaptureId) {
      clearUploadStallWatch();
      return undefined;
    }
    lastUploadByteProgressAtRef.current = Date.now();
    scheduleUploadStallWatch();
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      if (uploadPhaseRef.current !== 'uploading' || !activeCaptureIdRef.current) return;
      if (!uploadTaskRef.current) {
        void markUploadInterrupted();
        return;
      }
      const elapsed = Date.now() - (lastUploadByteProgressAtRef.current || 0);
      if (elapsed >= UPLOAD_BYTE_STALL_DETECT_MS) {
        void markUploadInterrupted();
        return;
      }
      scheduleUploadStallWatch();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearUploadStallWatch();
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [uploadPhase, activeCaptureId, clearUploadStallWatch, scheduleUploadStallWatch, markUploadInterrupted]);

  /* ------------------------------------------------------------------ */
  /*  startUpload                                                        */
  /* ------------------------------------------------------------------ */

  const startUpload = useCallback(
    async (file, captureId, userId, projectId, sourceMediaHints = null, shopifyProductId = null, uploadOptions = null) => {
      if (!captureId || !file) return;

      const pid = normalizedProjectId(projectId);
      const opts = uploadOptions && typeof uploadOptions === 'object' ? uploadOptions : {};
      const insvFiles = Array.isArray(opts.insvFiles) ? opts.insvFiles.filter(Boolean) : null;
      const sourceFormat = String(opts.sourceFormat || sourceMediaHints?.sourceFormat || '').toLowerCase();
      const isInsvUpload = sourceFormat === SOURCE_FORMAT.INSV && insvFiles && insvFiles.length > 0;
      const filesToUpload = isInsvUpload ? insvFiles : [file];
      const totalUploadBytes = filesToUpload.reduce(
        (sum, f) => sum + (typeof f?.size === 'number' ? f.size : 0),
        0,
      );

      if (uploadTaskRef.current) {
        try { uploadTaskRef.current.cancel(); } catch (_) { /* ignore */ }
        uploadTaskRef.current = null;
      }
      clearPipelinePollCache(captureId, pid);

      pipelineTimelineRef.current = createPipelineTimeline(captureId);
      workerProgressStartedRef.current = false;
      runpodJobStatusRef.current = null;
      logPipelineDiagnostic('upload_started', { captureId, projectId: pid, shopifyProductId });
      setActiveProjectId(pid);
      setActiveShopifyProductId(shopifyProductId ? String(shopifyProductId).trim() : null);
      activeStudioUidRef.current = userId || null;

      if (userId && auth.currentUser?.uid === userId) {
        try {
          await auth.currentUser.getIdToken();
        } catch (_) {
          /* Storage/Firestore may still proceed */
        }
      }

      const captureRef = userId
        ? doc(db, 'users', userId, STUDIO_COLLECTION, pid, CAPTURES_SUB, captureId)
        : null;
      if (captureRef) {
        try {
          await patchCaptureDoc(captureRef, {
            updatedAt: serverTimestamp(),
            'processing.status': 'processing',
            'processing.stage': deleteField(),
            'processing.failedAt': deleteField(),
            'processing.failStatus': deleteField(),
            'processing.failedStageIndex': deleteField(),
            'processing.failedPercent': deleteField(),
            'processing.failureCode': deleteField(),
            'processing.failureDetail': deleteField(),
            'processing.framesExtracted': deleteField(),
            'processing.framesRegistered': deleteField(),
            'processing.processtime': deleteField(),
            'processing.diagnostics': deleteField(),
            'progress.percent': deleteField(),
            'progress.message': deleteField(),
            'progress.queuePosition': deleteField(),
            status: deleteField(),
            failedAt: deleteField(),
            failStatus: deleteField(),
          });
        } catch (e) {
          console.error('Failed to clear capture failure before upload', e);
        }
      }
      const setCaptureStatusFailed = async (
        failStage = null,
        failedPercentOverride = null,
        verboseFailureDetail = null,
        { failureCode = null } = {},
      ) => {
        if (!captureRef) return;
        const now = Date.now();
        const prev = statusFailedThrottleRef.current;
        if (prev.captureId === captureId && now - prev.at < SET_CAPTURE_STATUS_FAILED_THROTTLE_MS) return;
        const localFailedPercent = Number.isFinite(failedPercentOverride)
          ? Math.round(Math.max(0, failedPercentOverride))
          : Math.round(
            Math.max(
              0,
              Number.isFinite(lastConfirmedPercentRef.current) ? lastConfirmedPercentRef.current : 0,
              Number.isFinite(pipelinePercentRef.current) ? pipelinePercentRef.current : 0,
              Number.isFinite(uploadPercentRef.current) ? uploadPercentRef.current : 0,
            ),
          );
        const failedStageIndex = failStage ? getFailedStageIndex(failStage) : -1;
        const resolvedFailureDetail = (() => {
          if (verboseFailureDetail != null && String(verboseFailureDetail).trim()) {
            const trimmed = String(verboseFailureDetail).trim();
            if (failureCode === DISPATCH_FAILED_FAILURE_CODE || isVerboseFailureText(trimmed)) {
              return trimmed;
            }
          }
          if (failStage && isVerboseFailureText(failStage)) {
            return String(failStage).trim();
          }
          return null;
        })();
        try {
          const failurePatch = buildFailureFirestorePatch({
            status: 'failed',
            failedAtRaw: failStage,
            failedStageIndex: failedStageIndex >= 0 ? failedStageIndex : null,
            failedPercent: localFailedPercent,
            progressPercent: localFailedPercent,
            failureDetail: resolvedFailureDetail,
          });
          if (failureCode) {
            failurePatch['processing.failureCode'] = failureCode;
          }
          await patchCaptureDoc(captureRef, {
            ...failurePatch,
            updatedAt: serverTimestamp(),
            status: deleteField(),
            failedAt: deleteField(),
            failStatus: deleteField(),
          });
          statusFailedThrottleRef.current = { captureId, at: Date.now() };
        } catch (e) {
          console.error('Failed to update capture status to failed', e);
        }
      };

      workerProgressStartedRef.current = false;
      runpodJobStatusRef.current = null;
      activeCaptureIdRef.current = captureId;
      setActiveCaptureId(captureId);
      setIsUploading(true);
      setUploadPhase('uploading');
      setUploadPercent(0);
      setPipelinePercent(0);
      setUploadBytesTransferred(0);
      setUploadTotalBytes(totalUploadBytes);
      setQueuePosition(null);
      setProgressPercent(0);
      setProgressMessage(isInsvUpload ? 'UPLOADING RAW INSV' : 'UPLOADING VIDEO');
      setUploadError(null);
      setCurrentStage(1);
      setSfmConductingPhase(null);
      setFinalPhase(null);
      if (sfmTimeoutRef.current) clearTimeout(sfmTimeoutRef.current);
      if (finalTimeoutRef.current) clearTimeout(finalTimeoutRef.current);
      clearHeartbeat();
      cleanupFirestoreListener();
      const now = Date.now();
      lastBackendEventAtRef.current = now;
      lastConfirmedEventAtRef.current = now;
      lastConfirmedPercentRef.current = 0;
      stageEnteredAtRef.current = now;

      let hasError = false;
      let pipelineAccepted = false;

      /* ---------- upload video to Firebase Storage ---------- */
      let token = null;
      try {
        token = await auth.currentUser?.getIdToken?.();
      } catch (_) {}
      const videoName = deriveVideoNameFromCaptureId(captureId);
      const storageBase = captureStoragePrefix(userId, pid, captureId);
      const uploadPath = isInsvUpload
        ? null
        : `${storageBase}/uploads/${videoName}.mp4`;
      try {
        if (!opts.isSampleVideo) {
          await assertCapturesStorageHeadroom(functions, totalUploadBytes);
        }
      } catch (e) {
        hasError = true;
        clearHeartbeat();
        cleanupFirestoreListener();
        setUploadError(e?.message || 'Storage check failed');
        setProgressMessage('Error');
        await setCaptureStatusFailed('Upload', uploadPercentRef.current);
        setIsUploading(false);
        setActiveCaptureId(null);
        setActiveProjectId(null);
        clearUploadSession();
        return;
      }
      const contentType = file?.type || (isInsvUpload ? 'application/octet-stream' : 'video/mp4');
      let aggregateTransferred = 0;
      const emitUploadByteProgress = (transferred, total) => {
        uploadProgressSnapshotRef.current = { transferred, total };
        const safeTotal = total > 0 ? total : totalUploadBytes;
        const pct = safeTotal > 0 ? Math.min(100, Math.round((transferred / safeTotal) * 100)) : 0;
        lastUploadByteProgressAtRef.current = Date.now();
        setUploadBytesTransferred(transferred);
        setUploadTotalBytes(safeTotal);
        setUploadPercent(pct);
        scheduleUploadStallWatch();
      };
      const scheduleUploadByteProgress = (transferred, total) => {
        uploadProgressSnapshotRef.current = { transferred, total };
        const now = Date.now();
        const elapsed = now - (scheduleUploadByteProgress.lastEmitAt || 0);
        if (elapsed >= UPLOAD_PROGRESS_THROTTLE_MS) {
          scheduleUploadByteProgress.lastEmitAt = now;
          emitUploadByteProgress(transferred, total);
          return;
        }
        if (uploadProgressThrottleRef.current) return;
        uploadProgressThrottleRef.current = setTimeout(() => {
          uploadProgressThrottleRef.current = null;
          scheduleUploadByteProgress.lastEmitAt = Date.now();
          const snap = uploadProgressSnapshotRef.current;
          emitUploadByteProgress(snap.transferred, snap.total);
        }, UPLOAD_PROGRESS_THROTTLE_MS - elapsed);
      };
      scheduleUploadByteProgress.lastEmitAt = 0;

      const uploadCancelledRef = { current: false };
      const isUploadCancelled = () => uploadCancelledRef.current;
      const uploadCapturePart = async (part, partPath, partContentType, partBaseTransferred) => {
        await uploadVideoResumableWithRetry({
          storage,
          uploadPath: partPath,
          file: part,
          contentType: partContentType,
          uploadTaskRef,
          isCancelled: isUploadCancelled,
          onByteProgress: (transferred) => {
            scheduleUploadByteProgress(partBaseTransferred + transferred, totalUploadBytes);
          },
          onRetry: (attempt, maxAttempts) => {
            setProgressMessage(`RETRYING UPLOAD (${attempt}/${maxAttempts})`);
          },
        });
      };
      try {
        for (let i = 0; i < filesToUpload.length; i += 1) {
          const part = filesToUpload[i];
          const partPath = isInsvUpload
            ? `${storageBase}/uploads_raw/${part.name}`
            : uploadPath;
          const partContentType = part?.type || contentType;
          await uploadCapturePart(part, partPath, partContentType, aggregateTransferred);
          aggregateTransferred += typeof part.size === 'number' ? part.size : 0;
        }
      } catch (e) {
        if (uploadTaskRef.current) uploadTaskRef.current = null;
        if (e?.code === 'storage/canceled') {
          hasError = true;
          clearHeartbeat();
          cleanupFirestoreListener();
          setUploadError('Cancelled by user');
          setProgressMessage('Cancelled');
          setUploadPhase('failed');
          setIsUploading(false);
          setActiveCaptureId(null);
          setActiveProjectId(null);
          clearUploadSession();
          return;
        }
        hasError = true;
        clearHeartbeat();
        cleanupFirestoreListener();
        setUploadError(formatVideoUploadErrorMessage(e));
        setProgressMessage('Error');
        setUploadPhase('failed');
        await setCaptureStatusFailed('Upload', uploadPercentRef.current);
        setIsUploading(false);
        setActiveCaptureId(null);
        setActiveProjectId(null);
        clearUploadSession();
        return;
      } finally {
        uploadCancelledRef.current = true;
        if (uploadProgressThrottleRef.current) {
          clearTimeout(uploadProgressThrottleRef.current);
          uploadProgressThrottleRef.current = null;
        }
      }
      emitUploadByteProgress(
        uploadProgressSnapshotRef.current.total || totalUploadBytes || 0,
        uploadProgressSnapshotRef.current.total || totalUploadBytes || 0,
      );
      setUploadBytesTransferred(0);
      setUploadTotalBytes(0);
      uploadProgressSnapshotRef.current = { transferred: 0, total: 0 };
      setUploadPhase('queueing');
      setPipelinePercent(0);
      lastConfirmedPercentRef.current = 0;
      setProgressMessage('QUEUEING');
      setCurrentStage(0);
      pipelineTimelineRef.current?.mark('upload_complete', {
        bytes: totalUploadBytes,
        shopifyProductId,
        sourceFormat: isInsvUpload ? SOURCE_FORMAT.INSV : SOURCE_FORMAT.VIDEO,
      });
      try {
        let hints = sourceMediaHints;
        if (!hints && captureRef) {
          const meta = await readVideoFileSourceMetadata(file);
          hints = {
            sourceVideoSizeBytes: typeof file.size === 'number' ? file.size : null,
            sourceVideoResolution: meta.sourceVideoResolution,
            sourceVideoDurationSeconds:
              meta.durationSeconds != null && Number.isFinite(meta.durationSeconds)
                ? Math.round(meta.durationSeconds * 10) / 10
                : null,
          };
        }
        let videoMeta = null;
        if (captureRef) {
          try {
            videoMeta = await extractVideoMetadata(file);
          } catch (_) {
            videoMeta = null;
          }
        }
        if (captureRef && (hints || videoMeta)) {
          const patch = { updatedAt: serverTimestamp() };
          if (hints) {
            if (hints.sourceVideoSizeBytes != null) {
              patch['processing.sourceVideoSizeBytes'] = hints.sourceVideoSizeBytes;
            }
            if (hints.sourceVideoResolution != null) {
              patch['processing.sourceVideoResolution'] = hints.sourceVideoResolution;
            }
            if (hints.sourceVideoDurationSeconds != null) {
              patch['processing.sourceVideoDurationSeconds'] = hints.sourceVideoDurationSeconds;
            }
            if (hints.captureProfile != null) {
              patch['processing.captureProfile'] = hints.captureProfile;
            }
            if (hints.sourceFormat != null) {
              patch['processing.sourceFormat'] = hints.sourceFormat;
            }
            if (hints.insvVendor != null) {
              patch['processing.insvVendor'] = hints.insvVendor;
            }
            if (hints.insvInputMode != null) {
              patch['processing.insvInputMode'] = hints.insvInputMode;
            }
            if (hints.insvSessionId != null) {
              patch['processing.insvSessionId'] = hints.insvSessionId;
            }
            if (Array.isArray(hints.insvRawPaths) && hints.insvRawPaths.length > 0) {
              patch['processing.insvRawPaths'] = hints.insvRawPaths.map(
                (name) => `${captureStoragePrefix(userId, pid, captureId)}/uploads_raw/${name}`,
              );
            }
          }
          if (videoMeta) {
            patch.videoMeta = {
              createdAt: videoMeta.createdAt,
              lat: videoMeta.lat,
              lng: videoMeta.lng,
              altitude: videoMeta.altitude,
              cameraModel: videoMeta.cameraModel,
              focalLength: videoMeta.focalLength,
            };
          }
          await patchCaptureDoc(captureRef, patch);
        }
        const gpsSidecarFile = opts.gpsSidecarFile;
        if (captureRef && gpsSidecarFile && userId) {
          try {
            const sidecarPath = `${captureStoragePrefix(userId, pid, captureId)}/data/${captureId}_gps.srt`;
            await uploadBytes(storageRef(storage, sidecarPath), gpsSidecarFile, {
              contentType: 'text/plain',
            });
            await patchCaptureDoc(captureRef, {
              gpsTrack: {
                format: 'dji_srt',
                storagePath: sidecarPath,
                uploadedAt: serverTimestamp(),
              },
            });
          } catch (sidecarErr) {
            console.warn('GPS sidecar upload failed', sidecarErr);
            toast.error('GPS sidecar upload failed. Capture will continue without DJI track alignment.');
          }
        }
        if (captureRef) {
          await patchCaptureDoc(captureRef, {
            updatedAt: serverTimestamp(),
            'processing.status': 'queued',
            'processing.failedAt': deleteField(),
            'processing.failStatus': deleteField(),
            'processing.failedStageIndex': deleteField(),
            'processing.failedPercent': deleteField(),
            status: deleteField(),
            failedAt: deleteField(),
            failStatus: deleteField(),
          });
        }
      } catch (e) {
        console.error('Failed to merge source video metadata after upload', e);
      }

      /* ====================================================== */
      /*  DISPATCHED MODE (production – Cloud Functions)         */
      /* ====================================================== */
      if (IS_DISPATCHED_MODE) {
        const onComplete = () => {
          if (stuckCheckTimeoutRef.current) {
            clearTimeout(stuckCheckTimeoutRef.current);
            stuckCheckTimeoutRef.current = null;
          }
          cleanupFirestoreListener();
          clearHeartbeat();
          clearUploadSession();
          setIsUploading(false);
          setUploadError(null);
          setActiveCaptureId(null);
          setActiveProjectId(null);
        };

        const onFail = async (
          msg,
          failStage,
          failedPercent = null,
          verboseFailureDetail = null,
          failMeta = {},
        ) => {
          hasError = true;
          if (stuckCheckTimeoutRef.current) {
            clearTimeout(stuckCheckTimeoutRef.current);
            stuckCheckTimeoutRef.current = null;
          }
          cleanupFirestoreListener();
          clearHeartbeat();
          setUploadPhase('failed');
          setUploadError(msg || 'Something went wrong');
          setProgressMessage(msg || 'Error');
          if (failStage) {
            await setCaptureStatusFailed(failStage, failedPercent, verboseFailureDetail, failMeta);
          }
        };

        // Subscribe to Firestore capture doc for real-time progress
        if (captureRef) {
          firestoreUnsubRef.current = onSnapshot(captureRef, (snap) => {
            if (!snap.exists()) return;
            const data = snap.data();
            const status = data?.processing?.status;
            lastCaptureProcessingStatusRef.current = status;
            lastCapturePipelineStageRef.current = data?.processing?.stage ?? null;
            const prog = data?.progress;
            const { framesExtracted: firestoreFramesExtracted } = readCaptureFrameFields(data?.processing || {});
            const workerFound = hasWorkerFoundEvidence({
              processingStatus: status,
              progressPercent: prog?.percent,
              progressMessage: prog?.message,
              runpodJobStatus: runpodJobStatusRef.current,
              framesExtracted: firestoreFramesExtracted,
            });

            if (status === 'completed') {
              if (stuckCheckTimeoutRef.current) {
                clearTimeout(stuckCheckTimeoutRef.current);
                stuckCheckTimeoutRef.current = null;
              }
              if (finalTimeoutRef.current) clearTimeout(finalTimeoutRef.current);
              setUploadPhase('done');
              setFinalPhase('gaussian-complete');
              setPipelinePercent(100);
              setProgressMessage('RECONSTRUCTION COMPLETE');
              setCurrentStage(6);
              finalTimeoutRef.current = setTimeout(() => {
                setFinalPhase('viewer-ready');
                onComplete();
              }, 1500);
              return;
            }

            if (status === 'failed') {
              if (stuckCheckTimeoutRef.current) {
                clearTimeout(stuckCheckTimeoutRef.current);
                stuckCheckTimeoutRef.current = null;
              }
              const failedAt = data?.processing?.failedAt;
              if (isStaleFirestorePipelineFailure(data, {
                progressMessage: data?.progress?.message,
              })) {
                setUploadError(null);
                const stageFromMsg = messageToStage(data?.progress?.message);
                setUploadPhase('pipeline');
                setPipelinePercent(Number(data?.progress?.percent) || 0);
                setProgressMessage(
                  normalizeProgressMessage(data?.progress?.message) || 'PROCESSING',
                );
                setCurrentStage(stageFromMsg >= 0 ? stageFromMsg : 0);
                return;
              }
              const isCancelled = failedAt === 'Cancelled';
              if (!isCancelled) {
                const failureDetail = data?.processing?.failureDetail;
                const failureCode = data?.processing?.failureCode;
                const failedPercent = Number.isFinite(data?.processing?.failedPercent)
                  ? Number(data.processing.failedPercent)
                  : (Number.isFinite(data?.progress?.percent) ? Number(data.progress.percent) : null);
                const resolvedDetail = resolveFailureDetailCalloutText({
                  failureDetail,
                  failureCode,
                  failedAt,
                  progressMessage: data?.progress?.message,
                  failedStageIndex: getFailedStageIndex(failedAt),
                });
                const failMsg = resolvedDetail
                  || (failureDetail && isVerboseFailureText(String(failureDetail))
                    ? String(failureDetail).trim()
                    : 'Processing failed. Open the capture to review what happened.');
                onFail(
                  failMsg,
                  failedAt || 'Unknown',
                  failedPercent,
                  resolvedDetail || (failureDetail && isVerboseFailureText(String(failureDetail))
                    ? String(failureDetail).trim()
                    : null),
                );
              } else {
                hasError = true;
                cleanupFirestoreListener();
                clearHeartbeat();
                setUploadError('Cancelled by user');
                setProgressMessage('Cancelled');
              }
              return;
            }

            if (status === 'queued' && !workerFound) {
              if (prog?.queuePosition != null) {
                setQueuePosition(Number(prog.queuePosition));
              } else {
                setQueuePosition(null);
              }
              setUploadPhase('queueing');
              setPipelinePercent(0);
              lastConfirmedPercentRef.current = 0;
              setProgressMessage('QUEUEING');
              setCurrentStage(-1);
              return;
            }

            if (firestoreFramesExtracted != null && Number.isFinite(Number(firestoreFramesExtracted))) {
              setFramesExtracted(Number(firestoreFramesExtracted));
            }

            if (workerFound && !workerProgressStartedRef.current) {
              workerProgressStartedRef.current = true;
              setUploadPhase('pipeline');
              setCurrentStage((prev) => (prev < 2 ? 2 : prev));
            }

            if (prog) {
              const eventNow = Date.now();
              lastBackendEventAtRef.current = eventNow;
              lastConfirmedEventAtRef.current = eventNow;

              if (prog.queuePosition != null) {
                setQueuePosition(Number(prog.queuePosition));
                setUploadPhase('queueing');
                setPipelinePercent(0);
                lastConfirmedPercentRef.current = 0;
                setProgressMessage('QUEUEING');
                setCurrentStage(-1);
                return;
              }
              setQueuePosition(null);

              const pct = Number.isFinite(prog.percent) ? prog.percent : 0;
              const queueSignal = isWorkerQueueingSignal(
                prog.message,
                runpodJobStatusRef.current,
                pct,
                workerProgressStartedRef.current,
                { frames: firestoreFramesExtracted, processingStatus: status },
              );
              if (queueSignal && pct === 0 && !workerFound) {
                setUploadPhase('queueing');
                setPipelinePercent(0);
                setProgressMessage('QUEUEING');
                setCurrentStage(-1);
                return;
              }
              if (workerFound && uploadPhaseRef.current === 'queueing') {
                setUploadPhase('pipeline');
                setCurrentStage((prev) => (prev < 2 ? 2 : prev));
              }
              if (pct > 0 && workerFound) {
                const confirmedPercent = Math.max(lastConfirmedPercentRef.current, pct);
                lastConfirmedPercentRef.current = confirmedPercent;
                setPipelinePercent((prev) => Math.max(prev, confirmedPercent));
              } else if (!workerFound && status === 'queued') {
                return;
              }

              if (pct > 3 && stuckCheckTimeoutRef.current) {
                clearTimeout(stuckCheckTimeoutRef.current);
                stuckCheckTimeoutRef.current = null;
              }

              if (prog.message) {
                const normalized = normalizeProgressMessage(prog.message);
                setProgressMessage(normalized);
                setCurrentStage((prev) => {
                  const next = Math.max(prev, messageToStage(prog.message));
                  if (next > prev) stageEnteredAtRef.current = Date.now();
                  return next;
                });

                const m = (prog.message || '').toLowerCase();
                if (m.includes('initiating') && m.includes('sfm')) {
                  if (sfmTimeoutRef.current) clearTimeout(sfmTimeoutRef.current);
                  setSfmConductingPhase('initiating');
                  sfmTimeoutRef.current = setTimeout(() => setSfmConductingPhase('conducting'), 2000);
                } else if (
                  m.includes('done converting video to images') ||
                  m.includes('extracting superpoint features') ||
                  m.includes('matching features with lightglue') ||
                  m.includes('running bundle adjustment') ||
                  (m.includes('sfm') && m.includes('complete'))
                ) {
                  setSfmConductingPhase('conducting');
                }
              }
            }
          }, (err) => {
            const code = err?.code || '';
            const msg = (err?.message || '').toLowerCase();
            if (code === 'unavailable' || msg.includes('offline') || msg.includes('failed to get document')) {
              return;
            }
            if (process.env.NODE_ENV === 'development') {
              // eslint-disable-next-line no-console
              console.warn('[UploadContext] Firestore capture listener', err?.code, err?.message);
            }
          });
        }

        // Dispatch via Cloud Function
        try {
          pipelineTimelineRef.current?.mark('dispatch_submit', { captureId, projectId: pid });
          logPipelineDiagnostic('dispatch_submit', { captureId, projectId: pid });
          const dispatchCaptureFn = httpsCallable(functions, 'dispatchCapture');
          const dispatchPayload = { captureId, projectId: pid, studioUid: userId };
          let result;
          try {
            result = await dispatchCaptureFn(dispatchPayload);
          } catch (firstDispatchErr) {
            if (!isRetryableDispatchCallableError(firstDispatchErr)) {
              throw firstDispatchErr;
            }
            logPipelineDiagnostic('dispatch_retry', {
              captureId,
              projectId: pid,
              code: firstDispatchErr?.code ?? null,
            });
            pipelineTimelineRef.current?.mark('dispatch_retry', {
              captureId,
              projectId: pid,
              code: firstDispatchErr?.code ?? null,
            });
            result = await dispatchCaptureFn(dispatchPayload);
          }
          pipelineAccepted = true;
          pipelineTimelineRef.current?.mark('dispatch_result', {
            status: result.data?.status ?? null,
            jobId: result.data?.jobId ?? null,
            dispatchMode: result.data?.dispatchMode ?? null,
          });
          logPipelineDiagnostic('dispatch_result', {
            captureId,
            projectId: pid,
            status: result.data?.status ?? null,
            jobId: result.data?.jobId ?? null,
            dispatchMode: result.data?.dispatchMode ?? null,
          });

          if (result.data?.status === 'queued') {
            setUploadPhase('queueing');
            setPipelinePercent(0);
            lastConfirmedPercentRef.current = 0;
            setProgressMessage('QUEUEING');
            setCurrentStage(-1);
          }

          // Auto-check and re-dispatch if RunPod job timed out while worker was pulling image.
          if (stuckCheckTimeoutRef.current) clearTimeout(stuckCheckTimeoutRef.current);
          stuckCheckTimeoutRef.current = setTimeout(async () => {
            stuckCheckTimeoutRef.current = null;
            const cid = activeCaptureIdRef.current;
            if (!cid || hasError) return;
            try {
              const checkFn = httpsCallable(functions, 'checkRunPodJobAndRedispatch');
              const res = await checkFn({
                captureId: cid,
                projectId: activeProjectIdRef.current ?? pid,
                studioUid: activeStudioUidRef.current ?? userId,
              });
              if (res.data?.redispatch) {
                setProgressMessage('Reconnecting to worker…');
              }
            } catch (e) {
              // Silently ignore – user can retry manually
            }
          }, RUNPOD_STUCK_REDISPATCH_MS);
        } catch (err) {
          if (!hasError) {
            const failureDetail = dispatchCallableFailureDetail(err);
            const userMsg = callableErrorMessage(err, TRANSPARENT_PROCESSING_FAILURE_MESSAGE);
            await onFail(userMsg, 'Dispatch', null, failureDetail, {
              failureCode: DISPATCH_FAILED_FAILURE_CODE,
            });
          }
          return;
        }
        return;
      }

      /* ====================================================== */
      /*  LOCAL MODE (development – HTTP + Redis status polling) */
      /* ====================================================== */
      let pollIntervalId = null;
      const cleanupListeners = () => {
        if (pollIntervalId != null) {
          clearInterval(pollIntervalId);
          pollIntervalId = null;
        }
      };

      /** Drives the in-tab upload + local-dev pipeline bar; studio CaptureViewer also uses `usePipelinePolling` when this session is not the active upload. */
      const applyPipelineStatus = (data) => {
        if (!data?.found) return;
        const st = (data.status || '').toLowerCase();

        if (st === 'cancelled') {
          hasError = true;
          clearHeartbeat();
          setUploadError('Cancelled by user');
          setProgressMessage('Cancelled');
          if (pipelineAccepted) cleanupListeners();
          return;
        }

        if (st === 'failed') {
          hasError = true;
          clearHeartbeat();
          const msg = (data.failed_at != null && String(data.failed_at))
            ? `Error: ${data.failed_at}`
            : 'Pipeline failed';
          setUploadError(msg);
          setProgressMessage(msg);
          setUploadPhase('failed');
          if (workerProgressStartedRef.current) {
            const rawFailedAt = data.failed_at != null ? String(data.failed_at).trim() : '';
            const strippedMsg = msg.replace(/^Error:\s*/i, '').trim();
            const verboseFailureDetail = isVerboseFailureText(rawFailedAt)
              ? rawFailedAt
              : (isVerboseFailureText(strippedMsg) ? strippedMsg : null);
            const failStage = rawFailedAt || inferFailStageFromError(msg) || null;
            const failedPercent = Number.isFinite(data.failed_percent)
              ? Number(data.failed_percent)
              : (Number.isFinite(data.percent) ? Number(data.percent) : null);
            setCaptureStatusFailed(failStage, failedPercent, verboseFailureDetail);
          }
          if (pipelineAccepted) cleanupListeners();
          return;
        }

        if (st === 'completed') {
          const eventNow = Date.now();
          lastBackendEventAtRef.current = eventNow;
          lastConfirmedEventAtRef.current = eventNow;
          lastConfirmedPercentRef.current = 100;
          setUploadPhase('done');
          setPipelinePercent(100);
          setProgressMessage('RECONSTRUCTION COMPLETE');
          setCurrentStage(6);
          if (finalTimeoutRef.current) clearTimeout(finalTimeoutRef.current);
          setFinalPhase('gaussian-complete');
          finalTimeoutRef.current = setTimeout(() => {
            setFinalPhase('viewer-ready');
            if (pipelineAccepted) {
              cleanupListeners();
              clearHeartbeat();
              clearUploadSession();
              setIsUploading(false);
              setUploadError(null);
              setActiveCaptureId(null);
              setActiveProjectId(null);
            }
          }, 1500);
          return;
        }

        const rp = String(data.runpodJobStatus || '').trim().toLowerCase();
        runpodJobStatusRef.current = rp || null;
        if (st === 'queued' || data.queued || rp === 'in_queue') {
          lastBackendEventAtRef.current = Date.now();
          setUploadPhase('queueing');
          setPipelinePercent(0);
          lastConfirmedPercentRef.current = 0;
          setProgressMessage('QUEUEING');
          setCurrentStage(-1);
          return;
        }

        const pct = Number.isFinite(data.percent) ? data.percent : 0;
        const { stageTitle: redisStageTitle } = mapRedisStageToFrontend(data.stage, pct);
        const queueSignal = isWorkerQueueingSignal(
          redisStageTitle,
          rp,
          pct,
          workerProgressStartedRef.current,
          { processingStatus: st },
        );
        if (queueSignal && pct === 0) {
          setUploadPhase('queueing');
          setPipelinePercent(0);
          setProgressMessage('QUEUEING');
          setCurrentStage(-1);
          return;
        }
        if (pct > 0 && !workerProgressStartedRef.current) {
          workerProgressStartedRef.current = true;
          lastConfirmedPercentRef.current = 0;
          setUploadPhase('pipeline');
          pipelineTimelineRef.current?.mark('first_worker_progress', {
            pct,
            source: 'redis_poll',
            stage: data.stage ?? null,
          });
        }
        const eventNow = Date.now();
        lastBackendEventAtRef.current = eventNow;
        lastConfirmedEventAtRef.current = eventNow;
        const confirmedPercent = Math.max(lastConfirmedPercentRef.current, pct);
        lastConfirmedPercentRef.current = confirmedPercent;
        setPipelinePercent((prev) => Math.max(prev, confirmedPercent));

        const msg = normalizeProgressMessage(redisStageTitle);
        setProgressMessage(msg);
        setCurrentStage((prev) => {
          const next = Math.max(prev, messageToStage(msg));
          if (next > prev) stageEnteredAtRef.current = Date.now();
          return next;
        });

        const m = msg.toLowerCase();
        const stTitle = redisStageTitle.toLowerCase();
        if (stTitle.includes('extracting frames')) {
          if (sfmTimeoutRef.current) clearTimeout(sfmTimeoutRef.current);
          setSfmConductingPhase('initiating');
          sfmTimeoutRef.current = setTimeout(() => setSfmConductingPhase('conducting'), 2000);
        } else if (stTitle.includes('structure') || stTitle.includes('sfm') || m.includes('structure from motion')) {
          setSfmConductingPhase('conducting');
        }
        if (stTitle.includes('extracting colmap') || m.includes('matching features') || m.includes('bundle adjustment')) {
          setSfmConductingPhase('conducting');
        }
        if (m.includes('sfm') && m.includes('complete')) {
          if (sfmTimeoutRef.current) clearTimeout(sfmTimeoutRef.current);
          setSfmConductingPhase(null);
        }
      };

      const pollOnce = async () => {
        const cid = activeCaptureIdRef.current;
        if (!cid || !backendBaseUrl) return;
        try {
          const url = `${backendBaseUrl.replace(/\/$/, '')}/pipeline/status/${encodeURIComponent(cid)}`;
          const authHeaders = await fetchPipelineStatusAuthHeaders(() =>
            auth.currentUser?.getIdToken() ?? Promise.resolve(null));
          const res = await fetch(url, { headers: authHeaders });
          if (!res.ok) return;
          const data = await res.json();
          applyPipelineStatus(data);
        } catch (_) {}
      };

      pollIntervalId = setInterval(pollOnce, 2000);
      pollOnce();

      const controller = new AbortController();
      abortControllerRef.current = controller;
      const pipelineTimeoutId = setTimeout(() => controller.abort(), UPLOAD_AND_PIPELINE_TIMEOUT_MS);

      // Emit an immediate upload-intent signal so backend logs appear right away.
      try {
        await fetch(`${backendBaseUrl}/train/init`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({ captureId, filename: file?.name || '' }),
        });
      } catch (_) {}

      try {
        const response = await fetch(`${backendBaseUrl}/train/from-storage`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({ captureId, projectId: pid }),
          signal: controller.signal,
        });
        clearTimeout(pipelineTimeoutId);

        const contentType = response.headers.get('content-type');
        let result;
        if (contentType && contentType.includes('application/json')) {
          result = await response.json();
        } else {
          await response.text();
          hasError = true;
          if (response.status === 404) {
            setUploadError(`Backend server not found at ${backendBaseUrl}/train/from-storage. Is the backend running?`);
          } else if (response.status >= 500) {
            setUploadError(`Backend server error (${response.status}). Check backend logs.`);
          } else {
            setUploadError(`Backend returned non-JSON response (${response.status}). Check backend configuration.`);
          }
          cleanupListeners();
          clearHeartbeat();
          return;
        }

        if (response.status === 202) {
          pipelineAccepted = true;
          return;
        }

        if (!response.ok) {
          hasError = true;
          clearHeartbeat();
          setUploadError(result.error || 'Upload failed');
          const failStage = result.failStage ?? inferFailStageFromError(result.error) ?? null;
          await setCaptureStatusFailed(failStage);
        }
      } catch (err) {
        clearTimeout(pipelineTimeoutId);
        if (pipelineAccepted) {
          return;
        }
        if (err.name !== 'AbortError') {
          hasError = true;
          clearHeartbeat();
          if (err.message.includes('JSON') || err.message.includes('<!DOCTYPE')) {
            setUploadError(isLocalhost ? `Backend server error: Received HTML instead of JSON. Is the backend running at ${backendBaseUrl}?` : 'Servers offline');
          } else if (err.message === 'Failed to fetch') {
            setUploadError('Servers unavailable. Try again shortly.');
          } else {
            setUploadError(err.message || 'Upload failed');
          }
          const serversUnreachable = err.message === 'Failed to fetch' || err.message.includes('JSON') || err.message.includes('<!DOCTYPE');
          if (!serversUnreachable) {
            await setCaptureStatusFailed(inferFailStageFromError(err.message) || null);
          }
        } else {
          if (err.name === 'AbortError') {
            hasError = true;
            clearHeartbeat();
            setUploadError('Request timed out');
          }
        }
        cleanupListeners();
      } finally {
        if (!pipelineAccepted) {
          cleanupListeners();
          clearHeartbeat();
          if (!hasError) {
            clearUploadSession();
            setIsUploading(false);
            setUploadError(null);
            setActiveCaptureId(null);
            setActiveProjectId(null);
          }
        }
      }
    },
    [backendBaseUrl, isLocalhost, clearHeartbeat, cleanupFirestoreListener, scheduleUploadStallWatch]
  );

  const setUploadFailed = useCallback((captureId, message) => {
    setActiveCaptureId(captureId);
    setIsUploading(false);
    setUploadError(message);
    setUploadPhase('failed');
    setUploadPercent(0);
    setPipelinePercent(0);
    setProgressPercent(0);
    setProgressMessage('Error');
    setCurrentStage(0);
    clearHeartbeat();
  }, [clearHeartbeat]);

  /** Apply Firestore capture progress into upload overlay state (CaptureViewer metadata listener). */
  const syncPipelineFromMetadata = useCallback((data) => {
    if (!data || typeof data !== 'object') return;
    const status = data?.processing?.status;
    const prog = data?.progress || {};
    const { framesExtracted: metadataFramesExtracted } = readCaptureFrameFields(data?.processing || {});
    const workerActive = isCapturePipelineWorkerActive(
      { processing: data.processing, progress: prog },
    );
    if (!workerActive) return;

    lastCaptureProcessingStatusRef.current = status;
    lastCapturePipelineStageRef.current = data?.processing?.stage ?? null;
    setUploadError(null);
    setQueuePosition(null);
    if (uploadPhaseRef.current === 'failed') {
      setUploadPhase('pipeline');
    } else if (uploadPhaseRef.current === 'queueing') {
      setUploadPhase('pipeline');
    }
    if (!workerProgressStartedRef.current) {
      workerProgressStartedRef.current = true;
    }
    if (metadataFramesExtracted != null && Number.isFinite(Number(metadataFramesExtracted))) {
      setFramesExtracted(Number(metadataFramesExtracted));
    }
    const pct = Number.isFinite(Number(prog.percent)) ? Number(prog.percent) : 0;
    if (pct > 0) {
      const confirmed = Math.max(lastConfirmedPercentRef.current, pct);
      lastConfirmedPercentRef.current = confirmed;
      setPipelinePercent((prev) => Math.max(prev, confirmed));
    }
    if (typeof prog.message === 'string' && prog.message.trim()) {
      setProgressMessage(normalizeProgressMessage(prog.message));
      setCurrentStage((prev) => {
        const next = Math.max(prev, messageToStage(prog.message));
        if (next > prev) stageEnteredAtRef.current = Date.now();
        return next < 0 ? Math.max(next, 2) : next;
      });
    } else if (Number.isFinite(Number(metadataFramesExtracted)) && Number(metadataFramesExtracted) > 0) {
      setCurrentStage((prev) => (prev < 2 ? 2 : prev));
    } else if ((status || '').toLowerCase() === 'processing') {
      setCurrentStage((prev) => (prev < 0 ? 2 : prev));
    }
  }, []);

  /**
   * After manual retry / GPU reconnect, re-bind upload overlay to this capture and
   * clear stale failure state so Firestore + poll updates can drive LoadingUI again.
   */
  const resumeCapturePipelineWatch = useCallback((captureId, userId, projectId, {
    cleanRetry = false,
    fullPipelineRestart = false,
    shopifyProductId = null,
  } = {}) => {
    if (!captureId || !userId) return;
    const pid = normalizedProjectId(projectId);
    setActiveCaptureId(captureId);
    setActiveProjectId(pid);
    activeStudioUidRef.current = userId;
    if (shopifyProductId) {
      setActiveShopifyProductId(String(shopifyProductId).trim());
    }
    setUploadError(null);
    setIsUploading(true);
    if (cleanRetry && !fullPipelineRestart) {
      // Exhaustive (stored-frame) SfM retry — skip back to structure-from-motion.
      setUploadPhase('pipeline');
      setProgressMessage('STRUCTURE FROM MOTION');
      setCurrentStage(3);
      setFramesExtracted(null);
    } else {
      // GPU reconnect or infra retry from uploaded video — queue first, then frame extraction.
      setUploadPhase('queueing');
      setProgressMessage('QUEUEING');
      setCurrentStage(-1);
      setFramesExtracted(null);
    }
    setQueuePosition(null);
    workerProgressStartedRef.current = false;
    runpodJobStatusRef.current = null;
    lastConfirmedPercentRef.current = 0;
  }, []);

  const value = useMemo(() => ({
    activeCaptureId,
    activeProjectId,
    activeShopifyProductId,
    isUploading,
    uploadPhase,
    uploadPercent,
    pipelinePercent,
    uploadBytesTransferred,
    uploadTotalBytes,
    queuePosition,
    progressPercent,
    progressMessage,
    currentStage,
    stageTitle,
    uploadError,
    framesExtracted,
    startUpload,
    cancelUpload,
    clearUpload,
    setUploadFailed,
    syncPipelineFromMetadata,
    resumeCapturePipelineWatch,
  }), [
    activeCaptureId,
    activeProjectId,
    activeShopifyProductId,
    isUploading,
    uploadPhase,
    uploadPercent,
    pipelinePercent,
    uploadBytesTransferred,
    uploadTotalBytes,
    queuePosition,
    progressPercent,
    progressMessage,
    currentStage,
    stageTitle,
    uploadError,
    framesExtracted,
    startUpload,
    cancelUpload,
    clearUpload,
    setUploadFailed,
    syncPipelineFromMetadata,
    resumeCapturePipelineWatch,
  ]);

  return <UploadContext.Provider value={value}>{children}</UploadContext.Provider>;
}

export function useUpload() {
  const context = useContext(UploadContext);
  if (!context) {
    throw new Error('useUpload must be used within an UploadProvider');
  }
  return context;
}
